/**
 * ClientShell - Shared layout wrapper for all client portal pages
 * Sidebar navigation, header with notifications, responsive
 */

import React, { useState, useEffect } from 'react';
import { navigate, logout, getClientData, clientFetch } from './api';

const NAV_ITEMS = [
  { path: '/client/dashboard', label: 'Dashboard', icon: '📊' },
  { path: '/client/onboarding', label: 'Onboarding', icon: '🚀' },
  { path: '/client/services', label: 'Services', icon: '⚡' },
  { path: '/client/store', label: 'Store', icon: '🛒' },
  { path: '/client/billing', label: 'Billing', icon: '💰' },
  { path: '/client/tickets', label: 'Support', icon: '🎫' },
  { path: '/client/call-us', label: 'Call Us', icon: '📞' },
  { path: '/client/documents', label: 'Documents', icon: '📄' },
  { path: '/client/contracts', label: 'Contracts', icon: '📋' },
  { path: '/client/knowledge-base', label: 'Help Center', icon: '📚' },
  { path: '/client/notification-center', label: 'Notifications', icon: '🔔' },
  { path: '/client/profile', label: 'Profile', icon: '👤' },
];

export default function ClientShell({ children, title }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const clientData = getClientData();
  const currentPath = window.location.pathname;

  useEffect(() => {
    clientFetch('/client/portal/notifications?unread_only=true')
      .then(r => r.json())
      .then(d => setUnreadCount(d.unreadCount || 0))
      .catch(() => {});
  }, []);

  const styles = {
    wrapper: {
      display: 'flex',
      minHeight: '100vh',
      backgroundColor: '#0B1120',
      color: '#FFFFFF',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    sidebar: {
      width: sidebarOpen ? 260 : 64,
      backgroundColor: '#111827',
      borderRight: '1px solid #1F2937',
      display: 'flex',
      flexDirection: 'column',
      transition: 'width 0.2s ease',
      position: 'fixed',
      top: 0,
      left: 0,
      bottom: 0,
      zIndex: 100,
    },
    logo: {
      padding: '20px 16px',
      borderBottom: '1px solid #1F2937',
      fontSize: sidebarOpen ? 16 : 12,
      fontWeight: 700,
      color: '#3B82F6',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
    },
    nav: {
      flex: 1,
      padding: '12px 8px',
      overflowY: 'auto',
    },
    navItem: (active) => ({
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '10px 12px',
      borderRadius: 8,
      marginBottom: 4,
      cursor: 'pointer',
      fontSize: 14,
      color: active ? '#FFFFFF' : '#9CA3AF',
      backgroundColor: active ? '#1E3A5F' : 'transparent',
      transition: 'all 0.15s ease',
    }),
    navIcon: {
      fontSize: 18,
      width: 24,
      textAlign: 'center',
      flexShrink: 0,
    },
    navLabel: {
      display: sidebarOpen ? 'block' : 'none',
      whiteSpace: 'nowrap',
    },
    main: {
      flex: 1,
      marginLeft: sidebarOpen ? 260 : 64,
      transition: 'margin-left 0.2s ease',
    },
    header: {
      padding: '16px 32px',
      borderBottom: '1px solid #1F2937',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: '#111827',
    },
    headerTitle: {
      fontSize: 20,
      fontWeight: 600,
    },
    headerRight: {
      display: 'flex',
      alignItems: 'center',
      gap: 16,
    },
    badge: {
      backgroundColor: '#EF4444',
      color: '#FFF',
      borderRadius: '50%',
      width: 20,
      height: 20,
      fontSize: 11,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      top: -4,
      right: -4,
    },
    content: {
      padding: '24px 32px',
      maxWidth: 1200,
    },
    logoutBtn: {
      padding: '8px 16px',
      borderRadius: 6,
      border: '1px solid #374151',
      backgroundColor: 'transparent',
      color: '#9CA3AF',
      cursor: 'pointer',
      fontSize: 13,
    },
    collapseBtn: {
      padding: '12px 16px',
      borderTop: '1px solid #1F2937',
      cursor: 'pointer',
      color: '#6B7280',
      fontSize: 13,
      textAlign: 'center',
    },
  };

  return (
    <div style={styles.wrapper}>
      <aside style={styles.sidebar}>
        <div style={styles.logo} onClick={() => navigate('/client/dashboard')}>
          <span>🏢</span>
          {sidebarOpen && <span>Talk Australia</span>}
        </div>
        <nav style={styles.nav}>
          {NAV_ITEMS.map((item) => {
            const isActive = currentPath === item.path;
            return (
              <div
                key={item.path}
                style={styles.navItem(isActive)}
                onClick={() => navigate(item.path)}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = '#1F2937'; }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <span style={styles.navIcon}>
                  {item.label === 'Notifications' && unreadCount > 0 ? (
                    <span style={{ position: 'relative' }}>
                      {item.icon}
                      <span style={styles.badge}>{unreadCount}</span>
                    </span>
                  ) : item.icon}
                </span>
                <span style={styles.navLabel}>{item.label}</span>
              </div>
            );
          })}
        </nav>
        <div style={styles.collapseBtn} onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? '◀ Collapse' : '▶'}
        </div>
      </aside>

      <main style={styles.main}>
        <header style={styles.header}>
          <div style={styles.headerTitle}>{title || 'Client Portal'}</div>
          <div style={styles.headerRight}>
            {clientData && (
              <span style={{ color: '#6B7280', fontSize: 13 }}>
                {clientData.company_name}
              </span>
            )}
            <button style={styles.logoutBtn} onClick={logout}>Sign Out</button>
          </div>
        </header>
        <div style={styles.content}>
          {children}
        </div>
      </main>
    </div>
  );
}
